import { useState, useEffect } from "react";
import { graphql, useMutation } from "react-relay";
import { RecordSourceSelectorProxy } from "relay-runtime";
import { toast, toastClearWaitingQueue } from "@olivahealth/oli-ui";

import { ResourceTrackingProperties } from "../../../types/tracking";
import { useAmplitude } from "../../../services/contexts/AmplitudeContext";
import useTranslation from "../../../hooks/useTranslation";
import { OlivaHook } from "../../../hooks/OlivaHook";
import {
  useMarkAsFavouriteButtonAddMutation as IAddMutation,
  useMarkAsFavouriteButtonAddMutation$data as IAddMutationData,
} from "./__generated__/useMarkAsFavouriteButtonAddMutation.graphql";
import {
  useMarkAsFavouriteButtonRemoveMutation as IRemoveMutation,
  useMarkAsFavouriteButtonRemoveMutation$data as IRemoveMutationData,
} from "./__generated__/useMarkAsFavouriteButtonRemoveMutation.graphql";

const useMarkAsFavouriteButtonAddMutation = graphql`
  mutation useMarkAsFavouriteButtonAddMutation($resourceId: ID!) {
    addResourceToFavourites(resourceId: $resourceId) {
      __typename
      ... on ResourceFavourited {
        resourceId
      }
    }
  }
`;

const useMarkAsFavouriteButtonRemoveMutation = graphql`
  mutation useMarkAsFavouriteButtonRemoveMutation($resourceId: ID!) {
    removeResourceFromFavourites(resourceId: $resourceId) {
      __typename
      ... on ResourceUnfavourited {
        resourceId
      }
    }
  }
`;

interface UseMarkAsFavouriteButtonEffects {
  toggleFavouriteStatus: (e?) => void;
}

interface UseMarkAsFavouriteButtonData {
  isFavourite: boolean;
}

type UseMarkAsFavouriteButton = OlivaHook<
  UseMarkAsFavouriteButtonData,
  UseMarkAsFavouriteButtonEffects
>;

export default function useMarkAsFavouriteButton({
  isFavourite: initialFavourite,
  resourceId,
  trackingProperties,
  onFavouriteToggle,
}: {
  isFavourite: boolean;
  resourceId: string;
  trackingProperties?: ResourceTrackingProperties;
  onFavouriteToggle?: () => void;
}): UseMarkAsFavouriteButton {
  const { trackEvent } = useAmplitude();
  const [markAsFavourite] = useMutation<IAddMutation>(
    useMarkAsFavouriteButtonAddMutation,
  );
  const { t } = useTranslation("common", {
    keyPrefix: "markAsFavouriteButton",
  });
  const [isFavourite, setIsFavourite] = useState<boolean>(initialFavourite);

  const [removeFromFavourite] = useMutation<IRemoveMutation>(
    useMarkAsFavouriteButtonRemoveMutation,
  );

  useEffect(() => {
    setIsFavourite(initialFavourite);
  }, [initialFavourite]);

  const toggleFavouriteStatus = (e?: Event) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (isFavourite) {
      setIsFavourite(false);
      removeFromFavourite({
        variables: { resourceId },

        onCompleted: (data) => {
          toastClearWaitingQueue();
          const hasError =
            data.removeResourceFromFavourites?.__typename !==
            "ResourceUnfavourited";

          if (hasError) {
            setIsFavourite(true);
          }
          toast({
            variant: hasError ? "error" : "success",
            body: t(hasError ? "remove.error" : "remove.success"),
          });

          trackEvent("Content removed from favourites", trackingProperties);

          if (!hasError) {
            onFavouriteToggle?.();
          }
        },
        updater: modifyLocalDataWithLatestUpdate,
      });
    } else {
      setIsFavourite(true);
      markAsFavourite({
        variables: { resourceId },
        onCompleted: (data) => {
          toastClearWaitingQueue();
          const hasError =
            data.addResourceToFavourites?.__typename !== "ResourceFavourited";

          if (hasError) {
            setIsFavourite(false);
          }
          toast({
            variant: hasError ? "error" : "success",
            body: t(hasError ? "add.error" : "add.success"),
          });

          trackEvent("Content added to favourites", trackingProperties);

          if (!hasError) {
            onFavouriteToggle?.();
          }
        },
        updater: modifyLocalDataWithLatestUpdate,
      });
    }
  };

  return {
    status: "success",
    data: {
      isFavourite,
    },
    effects: {
      toggleFavouriteStatus,
    },
  };
}

// Updates the isFavourite field on resource records in the Relay store
function modifyLocalDataWithLatestUpdate(
  store: RecordSourceSelectorProxy<IAddMutationData | IRemoveMutationData>,
  data: IAddMutationData | IRemoveMutationData | null | undefined,
) {
  if (!data) return;
  function isAddMutation(
    data: IAddMutationData | IRemoveMutationData,
  ): data is IAddMutationData {
    return (
      "addResourceToFavourites" in data &&
      data.addResourceToFavourites !== undefined
    );
  }

  if (isAddMutation(data)) {
    const resourceId =
      data.addResourceToFavourites.__typename === "ResourceFavourited" &&
      data.addResourceToFavourites?.resourceId;

    if (resourceId) {
      const resourceRecord = store.get(resourceId);
      if (resourceRecord) {
        resourceRecord.setValue(true, "isFavourite");
      }
    }
  } else {
    const resourceId =
      data.removeResourceFromFavourites.__typename === "ResourceUnfavourited" &&
      data.removeResourceFromFavourites?.resourceId;

    if (resourceId) {
      const resourceRecord = store.get(resourceId);
      if (resourceRecord) {
        resourceRecord.setValue(false, "isFavourite");
      }
    }
  }
}
